import { FluidTheme, FluidThemeInterface } from '@lmig/fluid-core';
import { Component, Element, h, Prop } from '@stencil/core';

import { Utils } from '../../utils/utils';

/**
 * @displayName Label
 *
 * @contextData If you have some text that represents a label for an input, you wrap it in label tags not for presentation but because that's what it is.
 * Without the label tag, that text is almost meaningless. With the label tag you are providing meaning and structure and forming a relationship between your markup
 * that can be better understood by computers/parsers/browsers/people.
 *
 * @builder true
 */
@Component({
  tag: 'fluid-label',
  styleUrl: 'fluid-label.less',
  shadow: true,
})
export class FluidLabel implements FluidThemeInterface {
  /**
   * What theme settings should the component use?
   * @type {string}
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLFluidLabelElement;

  /**
   * What should the label display as its primary text
   */
  @Prop({ mutable: true }) label: string;

  /**
   * Is the label a muted label
   */
  @Prop({ mutable: true }) muted: boolean;

  /**
   * Should the label be an inline label
   */
  @Prop({ mutable: true }) inline: boolean;

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
  }

  render() {
    const labelStyle = `fluid-label ${this.theme} ${
      this.muted ? 'muted' : ''
    } ${this.inline ? 'inline' : ''}`;
    return <label class={labelStyle}>{this.label}</label>;
  }
}
